import { Lightning, Router } from '@lightningjs/sdk'
import { getColor, grey300, grey50, grey920, grey960 } from '../../constants/colors'
import { UniNeueBold, UniNeueRegular } from '../../constants/fonts'
import { KEY_CODE } from '../../constants/videoPlayer'
import { Player } from '../../pages'
import { AppConfig } from '../../services/appConfig'
import { generateQR } from '../../utils/misc'
import PointerBackButton from '../NavigationPointers/PointerBackButton'

const FADE_ANIM_TIME = 0.5 // in seconds
const SLIDE_ANIM_TIME = 0.75 // in seconds

const SIDE_PANEL_WIDTH = 764

export default class SideModal extends Lightning.Component {
  private _animationFadeOut: Lightning.Animation
  private animationSlideFromLeft: Lightning.Animation

  static _template() {
    return {
      w: 1920,
      h: 1080,
      rect: true,
      color: getColor(grey960, 0.5),
      Container: {
        w: SIDE_PANEL_WIDTH + 1,
        h: 1080,
        x: 1920,
        rect: true,
        color: getColor(grey920),
        alpha: 1,
        shader: {
          type: Lightning.shaders.RoundedRectangle,
          radius: [40, 0, 0, 40],
        },
        flex: {},
        InnerContent: {
          y: 104,
          w: 560,
          flex: {
            direction: 'column',
          },
          flexItem: {
            margin: 80,
          },
          Title: {
            w: 560,
            text: {
              textColor: getColor(grey50),
              fontSize: 48,
              fontFace: UniNeueBold,
            },
          },
          Description: {
            w: 560,
            flexItem: {
              marginTop: 10,
            },
            text: {
              textColor: getColor(grey300),
              fontSize: 32,
              fontFace: UniNeueRegular,
              lineheight: 36,
            },
          },
          QRCode: {
            w: 512,
            h: 512,
            flexItem: {
              marginTop: 54,
              marginLeft: 10,
            },
            shader: null,
          },
          Footer: {
            w: 560,
            flex: {
              direction: 'row',
              alignItems: 'center',
              spacing: 30,
            },
            flexItem: {
              marginTop: 30,
            },
            children: [
              {
                type: Lightning.Text,
                ref: 'TextFooter',
                text: {
                  text: '',
                  textColor: getColor(grey300),
                  fontSize: 36.765,
                  fontFace: UniNeueRegular,
                  lineheight: 47.27,
                },
              },
              {
                type: Lightning.Text,
                ref: 'TextUrl',
                text: {
                  text: '',
                  textColor: getColor(grey50),
                  fontSize: 36.765,
                  fontFace: UniNeueBold,
                  lineheight: 47.27,
                },
              },
            ],
          },
        },
        PointerBackButton: {
          visible: true,
          type: PointerBackButton,
          x: (w) => w - 96,
          signals: {
            _handleBack: true,
          },
        },
      },
    }
  }

  get pointerBackButton() {
    return this.tag('PointerBackButton') as PointerBackButton
  }

  get title() {
    return this.tag('Title')
  }

  get description() {
    return this.tag('Description')
  }

  get qrCode() {
    return this.tag('QRCode')
  }

  get container() {
    return this.tag('Container')
  }

  get textFooter() {
    return this.tag('TextFooter')
  }

  get urlFooter() {
    return this.tag('TextUrl')
  }

  get footer() {
    return this.tag('Footer')
  }

  get page() {
    return Router.getActivePage()
  }

  set data(data: SideModalParams) {
    this.setMetadata(data)

    const { isCursorVisible } = AppConfig.shared.appSettings
    this.pointerBackButton.visible = isCursorVisible
  }

  async setMetadata(params: {
    title: string
    description: string
    url: string
    footer?: string[]
  }) {
    this.title.text.patch({
      text: params.title,
    })

    this.description.text.patch({
      text: params.description,
    })

    this.qrCode.patch({ src: await generateQR(params.url) })

    if (params.footer) {
      this.textFooter.patch({ text: { text: params.footer[0] } })
      this.urlFooter.patch({ text: { text: params.footer[1] } })
    } else {
      this.footer.visible = false
    }

    this.container.alpha = 1
    this.animationSlideFromLeft.replay()
  }

  _setup() {
    this._animationFadeOut = this.animation({
      duration: FADE_ANIM_TIME,
      actions: [{ t: 'Container', p: 'alpha', rv: 0, v: { 0: { v: 1 }, 1: { v: 0 } } }],
    })
    this.animationSlideFromLeft = this.animation({
      duration: SLIDE_ANIM_TIME,
      actions: [
        {
          t: 'Container',
          p: 'x',
          rv: 1920 - SIDE_PANEL_WIDTH,
          v: { 0: { v: 1920 }, 1: { v: 1920 - SIDE_PANEL_WIDTH } },
        },
      ],
    })

    // Only when the modal is gone we set the focus back to the player
    this._animationFadeOut.on('finish', () => {
      Router.focusPage()
    })
  }

  // Override any key presses
  _handleKey({ keyCode }) {
    switch (keyCode) {
      case KEY_CODE.RW:
      case KEY_CODE.MEDIA_RW:
      case KEY_CODE.PLAY_PAUSE:
      case KEY_CODE.MEDIA_PLAY_PAUSE:
      case KEY_CODE.MEDIA_PLAY:
      case KEY_CODE.MEDIA_PAUSE:
      case KEY_CODE.FF:
      case KEY_CODE.MEDIA_FF:
      case KEY_CODE.MEDIA_STOP:
        // Side modal is only used by player as of now
        this._handleBack(null)
        ;(this.page as Player).controls?._handleKey?.({ keyCode })
        break
      default:
        return true
    }
  }

  _handleBack(event) {
    event?.preventDefault()
    this._animationFadeOut.replay()

    return true
  }
}
